import { AlertCircle } from "lucide-react";
import type { FieldError as RHFFieldError } from "react-hook-form";

/**
 * Stable id for a field's error message, derived from the field name. Used to
 * wire the input's `aria-describedby` to the `<FieldError>` it renders.
 */
export function fieldErrorId(name: string): string {
  return `${name}-error`;
}

/**
 * ARIA props to spread onto an input that may show a FieldError. When an error
 * is present the input is marked invalid and pointed at its error message so
 * screen readers announce the problem (WCAG 3.3.1, 4.1.2). Pair with
 * `<FieldError id={fieldErrorId(name)} error={error} />`.
 *
 *   <Input {...register("email")} {...fieldErrorAria("email", errors.email)} />
 *   <FieldError id={fieldErrorId("email")} error={errors.email} />
 *
 * `hintId` keeps a persistent helper-text reference (e.g. a password rule) wired
 * alongside the error so neither description is lost.
 */
export function fieldErrorAria(name: string, error?: RHFFieldError, hintId?: string) {
  const describedBy = [hintId, error?.message ? fieldErrorId(name) : undefined]
    .filter(Boolean)
    .join(" ");
  return {
    ...(error?.message ? { "aria-invalid": true } : {}),
    ...(describedBy ? { "aria-describedby": describedBy } : {})
  };
}

/**
 * Inline error helper — renders nothing when no error, otherwise a readable,
 * announced message. `role="alert"` makes assistive tech speak it the moment it
 * appears; the icon + text means the meaning is never colour-only (WCAG 1.4.1),
 * and `text-sm` keeps it legible for low-vision users (the old `text-xs` was too
 * small). Pass `id` so the related input can reference it via aria-describedby.
 */
export function FieldError({ id, error }: { id?: string; error?: RHFFieldError }) {
  if (!error?.message) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 flex items-start gap-1.5 text-sm font-medium text-destructive">
      <AlertCircle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{String(error.message)}</span>
    </p>
  );
}
